import { z } from 'zod';

const safeIdentifier = z.string().min(1).max(80).regex(/^[a-z0-9]+(?:[._:-][a-z0-9]+)*$/);
const publicEntityReference = z.string().max(80).regex(/^(?:0x[a-fA-F0-9]{40,64}|[0-9a-fA-F-]{36})$/);
const destinationHost = z.string().min(1).max(253)
  .regex(/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/);

export const analyticsConsentSchema = z.strictObject({
  granted: z.literal(true),
  purpose: z.literal('product-improvement'),
  version: z.string().min(1).max(32).regex(/^[a-zA-Z0-9._-]+$/),
});

/** Deliberately excludes free text, URLs, search queries, form values and error
 * messages. Product analytics is not an authorization or economic evidence path.
 */
export const productAnalyticsEventSchema = z.strictObject({
  eventId: z.string().uuid(),
  occurredAt: z.iso.datetime(),
  eventType: z.enum([
    'page_view',
    'panel_view',
    'funnel_step',
    'ui_action',
    'search_summary',
    'client_error',
    'performance',
    'outbound_click',
    'execution_mode_view',
  ]),
  routeId: safeIdentifier,
  surfaceId: safeIdentifier.optional(),
  journeyId: safeIdentifier.optional(),
  stepId: safeIdentifier.optional(),
  actionId: safeIdentifier.optional(),
  entityKind: z.enum(['token', 'launch', 'vault', 'transaction', 'order']).optional(),
  entityId: publicEntityReference.optional(),
  executionMode: z.enum(['vault', 'direct-wallet']).optional(),
  metric: z.enum(['navigation_ms', 'render_ms', 'api_ms', 'interaction_ms']).optional(),
  value: z.number().finite().nonnegative().max(86_400_000).optional(),
  queryLength: z.number().int().nonnegative().max(256).optional(),
  resultCount: z.number().int().nonnegative().max(1_000_000).optional(),
  errorCode: safeIdentifier.optional(),
  destinationHost: destinationHost.optional(),
  destinationKind: z.enum(['docs', 'explorer', 'social', 'community', 'partner']).optional(),
});

export const productAnalyticsBatchSchema = z.strictObject({
  schemaVersion: z.literal(1),
  clientId: z.string().uuid(),
  sessionId: z.string().uuid(),
  experienceId: safeIdentifier.default('hoodmorning'),
  channelId: safeIdentifier.default('first-party'),
  consent: analyticsConsentSchema,
  events: z.array(productAnalyticsEventSchema).min(1).max(20),
});

export const productAnalyticsIdentifySchema = z.strictObject({
  schemaVersion: z.literal(1),
  clientId: z.string().uuid(),
  consent: analyticsConsentSchema,
});

export type ProductAnalyticsBatch = z.infer<typeof productAnalyticsBatchSchema>;
